import React, {useState, useEffect, useContext} from 'react';
import * as R from 'ramda';
import {Maybe} from 'monet';
import {useTranslation} from 'react-i18next';
import BootstrapTable from 'react-bootstrap-table-next';
import querystring from 'qs';
import paginationFactory, {
  PaginationProvider,
  SizePerPageDropdownStandalone,
  PaginationTotalStandalone,
  PaginationListStandalone,
} from 'react-bootstrap-table2-paginator';
import {Row, Col} from 'reactstrap';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import {toDollar} from '@/util';

import {SortableLabel} from '@/views/Layout/b2b/SortableList';
import Icon from '@/views/shared/Icon';
import DownloadFileButton from '@/views/shared/DownloadFileButton';

import Pop from '@/b2b/components/Pop';
import PolicyStatement from '@/b2b/pages/PolicyStatement';
import styles from '@/b2b/index.module.css';

import DocumentList from '@/b2b/pages/Clients/DocumentList';
import ClientDetail from '@/b2b/pages/Clients/ClientDetail';

import useAPI from '@/hooks/useAPI';
import {clientsList} from '@/b2b/apis';
import {ClientsCtx} from '@/b2b/contexts';
import LoadingOverlay from 'react-loading-overlay';

const ClientList = () => {
  const {t: tr} = useTranslation();
  const subtr = tr('clients:table_fields');

  const [{paging, queryParams}, dispatch] = useContext(ClientsCtx);

  const {result, loading, trigger} = useAPI(clientsList, {pending: true});

  const {items = [], totalSize = 0} = R.pathOr({}, ['data'], result);

  useEffect(
    () => {
      trigger(queryParams);
    },
    [queryParams]
  );

  const [selectedList, setSelectedList] = useState([]);
  const handleSelectRow = (row, isSelected) => {
    R.compose(
      setSelectedList,
      R.ifElse(
        R.equals(true),
        R.always(R.append(row.capId, selectedList)),
        R.always(R.filter(x => x !== row.capId, selectedList))
      )
    )(isSelected);
  };

  const handleSelectAll = (isSelected, rows) => {
    R.compose(
      setSelectedList,
      R.ifElse(
        R.equals(true),
        R.always(R.map(x => x.capId, rows)),
        R.always([])
      )
    )(isSelected);
  };

  const selectRowProp = {
    mode: 'checkbox',
    onSelect: (row, isSelected) => {
      handleSelectRow(row, isSelected);
    },
    onSelectAll: handleSelectAll,
  };

  const columns = [
    {
      dataField: 'category',
      text: '',
      formatter: (cell, row) => (
        <>
          <SortableLabel shortCode={row.category} />
          <SortableLabel shortCode={row.sourceTypeT} />
        </>
      ),
      headerStyle: {width: '3%'},
    },
    {
      dataField: 'clientName',
      text: subtr.client,
      headerStyle: {width: '18%'},
      sort: true,
    },
    {
      dataField: 'capId',
      text: '',
      formatter: (cell, row) => (
        <Pop
          provider={{component: ClientDetail, capId: row.capId}}
          icon='info'
        />
      ),
      headerStyle: {width: '5%'},
    },
    {
      dataField: 'policyNo',
      text: subtr.policy_no,
      sort: true,
      formatter: (cell, row) => (
        <div>
          <span>
            <b>{row.policyNo}</b>
          </span>
          <br />
          <span>{row.productName}</span>
        </div>
      ),
      headerStyle: {width: '15%'},
    },
    {
      dataField: 'insuredName',
      text: subtr.insured,
      headerStyle: {width: '12%'},
    },
    {
      dataField: 'modalPremium',
      text: subtr.modal_premium,
      formatter: (cell, row) =>
        R.join(' ', [row.currency, toDollar(row.modalPremium)]),
      headerStyle: {width: '10%'},
    },
    {
      dataField: 'policyStatus',
      text: subtr.status,
      sort: true,
      headerStyle: {width: '10%'},
    },
    {
      dataField: 'consultant',
      text: subtr.consultant,
      sort: true,
      headerStyle: {width: '13%'},
    },
    {
      dataField: 'accountNo',
      text: subtr.details,
      formatter: (cell, row) =>
        Maybe.fromFalsy(row.accountNo)
          .map(() => (
            <Pop
              provider={{component: DocumentList, accountNo: row.accountNo}}
              icon='inbox'
            />
          ))
          .orNull(),
      style: {textAlign: 'right', paddingRight: '2px'},
    },
    {
      dataField: 'stmtId',
      text: '',
      formatter: (cell, row) =>
        Maybe.fromFalsy(row.stmtId)
          .map(() => (
            <Pop
              provider={{component: PolicyStatement, partyId: row.stmtId}}
              icon='align-justify'
            />
          ))
          .orNull(),
      style: {textAlign: 'left', paddingLeft: '2px', width: '5%'},
    },
  ];

  useEffect(
    () => {
      setSelectedList([]);
    },
    [items]
  );

  const paginationOpts = {
    custom: true,
    totalSize,
    page: paging.page,
    paginationTotalRenderer: (from, to, size) => (
      <span>{tr('paginate_status', {from, to, size})}</span>
    ),
    sizePerPageList: [10, 20, 30],
  };

  const url = () =>
    `/b2b/client_export?${querystring.stringify(
      R.omit(['page', 'sizePerPage'], queryParams),
      {arrayFormat: 'brackets'}
    )}`;

  const statementUrl = type => {
    const isIncluded = capId => R.includes(capId, selectedList);

    const path = `/statement/download_${type}?${querystring.stringify(
      R.compose(
        R.omit(['page', 'sizePerPage']),
        R.merge({
          party_ids: R.join(
            ',',
            R.filter(isIncluded, R.map(x => x.capId, items))
          ),
        })
      )(queryParams)
    )}`;
    return path;
  };

  const onTableChange = (type, {page, sizePerPage, sortField, sortOrder}) => {
    dispatch({
      type: 'changePaging',
      payload: {page, sizePerPage, sortField, sortOrder},
    });
  };

  return (
    <LoadingOverlay active={loading} spinner text={tr('loading')}>
      <PaginationProvider pagination={paginationFactory(paginationOpts)}>
        {({paginationProps, paginationTableProps}) => (
          <>
            <BootstrapTable
              bootstrap4
              remote
              keyField='capId'
              selectRow={selectRowProp}
              data={items}
              columns={columns}
              bordered={false}
              onTableChange={onTableChange}
              {...paginationTableProps}
            />
            <Row>
              <Col md='3'>
                <DownloadFileButton
                  className={`btn btn-primary btn-sm ${styles.btn_sm}`}
                  disabled={R.equals(0, R.length(selectedList))}
                  path={statementUrl('pdf')}
                >
                  <span className='pr-2'>
                    <Icon icon='file-pdf' />
                  </span>
                  {tr('common:pdf_version')}
                </DownloadFileButton>{' '}
                <DownloadFileButton
                  className={`btn btn-primary btn-sm ${styles.btn_sm}`}
                  disabled={R.equals(0, R.length(selectedList))}
                  path={statementUrl('zip')}
                >
                  <span className='pr-2'>
                    <Icon icon='file-archive' />
                  </span>
                  {tr('common:zip_version')}
                </DownloadFileButton>
                <br />
                <PaginationTotalStandalone {...paginationProps} />
              </Col>
              <Col md='3 text-center' className={styles.pagination}>
                <SizePerPageDropdownStandalone
                  {...paginationProps}
                  btnContextual={`btn btn-primary btn-sm ${styles.btn_sm}`}
                />{' '}
                {tr('per_page')}
              </Col>
              <Col md='4 text-right' className={styles.pagination_list}>
                <PaginationListStandalone {...paginationProps} />
              </Col>
              <Col md='2 text-right'>
                <DownloadFileButton
                  className={`btn btn-outline-primary btn-sm ${styles.btn_sm}`}
                  path={url()}
                >
                  <span className='pr-2'>
                    <Icon icon='file-excel' />
                  </span>
                  {`${tr('sr:export_excel')} (${totalSize})`}
                </DownloadFileButton>
              </Col>
            </Row>
          </>
        )}
      </PaginationProvider>
    </LoadingOverlay>
  );
};

export default ClientList;
